/**
 * Formats a JSON string or object into a pretty-printed string
 * Preserves decimal zeros in numbers that originally had .0 (e.g., 1.0 stays as 1.0)
 * @param input - String to parse as JSON, object to stringify, or undefined
 * @returns Formatted JSON string or string representation of input
 */
export const formatJsonString = (
  input: string | object | null | undefined
): string => {
  if (input === null || input === undefined) {
    return String(input);
  }

  if (typeof input === 'string' && input.trim() === '') {
    return '';
  }

  // Check if string looks like JSON before attempting to parse
  if (typeof input === 'string') {
    const trimmed = input.trim();
    // JSON strings typically start with { or [ and end with } or ]
    const looksLikeJson =
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'));

    // If it doesn't look like JSON, return as-is
    if (!looksLikeJson) {
      return input;
    }
  }

  try {
    const originalString =
      typeof input === 'string' ? input : JSON.stringify(input);
    const obj = typeof input === 'string' ? JSON.parse(input) : input;
    let result = JSON.stringify(obj, null, 2);

    if (typeof input === 'string') {
      const decimalZeros = new Set<string>();
      const regex = /"([^"]+)":\s*(-?\d+)\.0+(?=\s*[,\n\]}])/g;
      let match = regex.exec(originalString);
      while (match !== null) {
        decimalZeros.add(`${match[1]}:${match[2]}`);
        match = regex.exec(originalString);
      }

      if (decimalZeros.size > 0) {
        const stringRegions: Array<{ start: number; end: number }> = [];
        let inString = false;
        let stringStart = -1;
        let escapeNext = false;

        for (let i = 0; i < result.length; i += 1) {
          const char = result[i];
          if (char === '\\' && inString) {
            escapeNext = !escapeNext;
          } else {
            if (char === '"' && !escapeNext) {
              const beforePos = Math.max(0, i - 20);
              const beforeQuote = result.substring(beforePos, i);
              if (beforeQuote.match(/:\s*$/)) {
                inString = true;
                stringStart = i;
              } else if (inString) {
                stringRegions.push({ start: stringStart, end: i });
                inString = false;
              }
            }
            escapeNext = false;
          }
        }

        const isInsideString = (pos: number): boolean =>
          stringRegions.some(
            region => pos >= region.start && pos <= region.end
          );

        const matches: Array<{
          match: string;
          key: string;
          number: string;
          suffix: string;
          index: number;
        }> = [];

        const resultRegex = /"([^"]+)":\s*(-?\d+)(\s*[,\n\]}])/g;
        let resultMatch = resultRegex.exec(result);
        while (resultMatch !== null) {
          if (!isInsideString(resultMatch.index)) {
            matches.push({
              match: resultMatch[0],
              key: resultMatch[1],
              number: resultMatch[2],
              suffix: resultMatch[3],
              index: resultMatch.index
            });
          }
          resultMatch = resultRegex.exec(result);
        }

        for (let i = matches.length - 1; i >= 0; i -= 1) {
          const m = matches[i];
          if (decimalZeros.has(`${m.key}:${m.number}`)) {
            result = `${result.substring(
              0,
              m.index
            )}"${m.key}": ${m.number}.0${m.suffix}${result.substring(
              m.index + m.match.length
            )}`;
          }
        }
      }
    }

    return result;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('formatJsonString: Failed to parse/stringify input:', error);
    return String(input);
  }
};
